import { useCallback, useEffect, useState } from 'react';
import { Building2, Plus, MapPin, Users, Pencil, Power } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { resolveOrganizationBrandName } from '../../utils/branding';
import CreateVirtualGymModal from '../admin/CreateVirtualGymModal';
import EditHoldingVirtualGymModal from '../admin/EditHoldingVirtualGymModal';

function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.data)) return payload.data;
  }
  return [];
}

function gymKindLabel(gym, t) {
  if (gym.gym_kind === 'd28d_anchor') {
    return t('holding.gym_kind_d28d', 'Fit Live · Programa');
  }
  return t('holding.gym_kind_virtual', 'Gimnasio virtual');
}

function isGymActive(gym) {
  return gym.status !== 'inactive' && gym.activo !== false;
}

/** Home del holding (admin_marca): solo provisiona gimnasios virtuales. */
export default function HoldingHomeCenter({ onNavigate }) {
  const { t } = useI18n();
  const [company, setCompany] = useState(null);
  const [virtualGyms, setVirtualGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingGym, setEditingGym] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [companyRes, gymsRes] = await Promise.all([
        api.get('/companies/mine').catch(() => ({ data: {} })),
        api.get('/companies/mine/virtual-gyms'),
      ]);
      setCompany(companyRes.data?.company || null);
      setVirtualGyms(asArray(gymsRes.data));
    } catch (err) {
      setError(err.response?.data?.error || t('holding.error_loading', 'No se pudo cargar el holding'));
      setVirtualGyms([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleStatus = async (gym) => {
    const active = isGymActive(gym);
    const nextStatus = active ? 'inactive' : 'active';
    if (nextStatus === 'inactive') {
      const ok = window.confirm(
        t(
          'holding.deactivate_confirm',
          '¿Inactivar "{name}"? Sus usuarios no podrán operar con normalidad hasta reactivarlo.',
        ).replace('{name}', resolveOrganizationBrandName({ entity: gym }) || gym.nombre || ''),
      );
      if (!ok) return;
    }
    setTogglingId(gym.id);
    setError('');
    try {
      await api.put(`/gyms/${gym.id}`, { status: nextStatus });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('holding.status_error', 'No se pudo cambiar el estado'));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <header className="surface-readable rounded-2xl border border-slate-700/40 bg-slate-900/60 p-6">
        <p className="text-xs uppercase tracking-wide text-emerald-400/90 font-semibold mb-1">
          {t('holding.badge', 'Holding')}
        </p>
        <h1 className="text-2xl font-bold text-white">
          {company?.nombre || t('holding.title', 'Mi holding')}
        </h1>
        <p className="text-sm text-slate-300 mt-2 max-w-2xl">
          {t(
            'holding.subtitle',
            'Desde aquí creas gimnasios virtuales y entregas credenciales a su administrador. Cada gym opera su Fit Live · Programa (maestros, sedes, clases) de forma independiente.',
          )}
        </p>
        <button
          type="button"
          className="btn-primary mt-4 inline-flex items-center gap-2"
          onClick={() => setCreateOpen(true)}
        >
          <Plus size={18} />
          {t('holding.create_virtual_gym', 'Crear gimnasio virtual')}
        </button>
      </header>

      {error ? <div className="alert-error text-sm">{error}</div> : null}

      <section className="surface-readable rounded-2xl border border-slate-700/40 bg-slate-900/40 p-5">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Building2 size={20} className="text-emerald-400" />
          {t('holding.virtual_gyms', 'Gimnasios virtuales')}
        </h2>
        {loading ? (
          <p className="text-sm text-slate-400">{t('common.loading', 'Cargando...')}</p>
        ) : virtualGyms.length === 0 ? (
          <p className="text-sm text-slate-400">
            {t('holding.no_gyms', 'Aún no hay gimnasios virtuales. Crea el primero con el botón de arriba.')}
          </p>
        ) : (
          <ul className="space-y-3">
            {virtualGyms.map((gym) => {
              const active = isGymActive(gym);
              return (
              <li
                key={gym.id}
                className={`rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${
                  active
                    ? 'border-slate-700/50 bg-slate-950/50'
                    : 'border-rose-900/40 bg-rose-950/20 opacity-90'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-white">{resolveOrganizationBrandName({ entity: gym })}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${
                      gym.gym_kind === 'd28d_anchor'
                        ? 'bg-amber-500/15 text-amber-200 border-amber-500/30'
                        : 'bg-sky-500/15 text-sky-200 border-sky-500/30'
                    }`}>
                      {gymKindLabel(gym, t)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                    {gym.ciudad ? (
                      <span className="inline-flex items-center gap-1"><MapPin size={12} />{gym.ciudad}</span>
                    ) : null}
                    <span className="inline-flex items-center gap-1">
                      <Users size={12} />
                      {t('holding.sedes_count', '{n} sedes').replace('{n}', String(gym.sedes_count ?? 0))}
                    </span>
                    {gym.admin_email ? (
                      <span className="text-slate-500">
                        {t('holding.operator', 'Operador')}: <span className="text-slate-300">{gym.admin_email}</span>
                      </span>
                    ) : null}
                  </p>
                  {gym.invite_code ? (
                    <p className="text-xs text-emerald-400/90 mt-1 font-mono">{gym.invite_code}</p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full border ${
                    active
                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                      : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                  }`}>
                    {active ? t('holding.status_active', 'Activo') : t('holding.status_inactive', 'Inactivo')}
                  </span>
                  <button
                    type="button"
                    className="btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                    onClick={() => setEditingGym(gym)}
                    title={t('holding.edit_gym', 'Editar gimnasio virtual')}
                  >
                    <Pencil size={14} />
                    {t('common.edit', 'Editar')}
                  </button>
                  <button
                    type="button"
                    className={`text-xs px-3 py-1.5 inline-flex items-center gap-1.5 rounded-lg border transition-colors ${
                      active
                        ? 'border-rose-500/40 text-rose-300 hover:bg-rose-500/10'
                        : 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
                    }`}
                    onClick={() => handleToggleStatus(gym)}
                    disabled={togglingId === gym.id}
                    title={active
                      ? t('holding.deactivate', 'Inactivar')
                      : t('holding.activate', 'Activar')}
                  >
                    <Power size={14} />
                    {togglingId === gym.id
                      ? t('common.saving', 'Guardando…')
                      : (active ? t('holding.deactivate', 'Inactivar') : t('holding.activate', 'Activar'))}
                  </button>
                </div>
              </li>
            );})}
          </ul>
        )}
      </section>

      <CreateVirtualGymModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      <EditHoldingVirtualGymModal
        gym={editingGym}
        open={Boolean(editingGym)}
        onClose={() => setEditingGym(null)}
        onSaved={load}
      />
    </div>
  );
}
